import React, {RefObject} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {
    KatButton,
    KatButtonGroup,
    KatCheckbox,
    KatExpander,
    KatInputGroup,
    KatLabel,
    KatLink,
    KatPopover,
} from "@amzn/katal-react";
import { FeeInfo, FeeType, ProgramFeeResult } from "src/model/ProgramFees"
import MetricsUtil from "src/utils/MetricsUtil";
import {getCurrencySymbolConfig, getCurrencyUnitConfig, getHelpPageLink} from "src/config/Config";
import {getCurrencyString} from "../../utils/ProgramDataUtil"
import CurrencyItem from "src/model/CurrencyItem";
import NumberKatInput from "../common/NumberKatInput";

export interface ProgramCostProps {
    isMFN: boolean;
    productPrice: CurrencyItem,
    countryCode: string;
    programFeeResult: ProgramFeeResult,
    handleChangeCostPerUnit: Function,
}

interface OwnProps {
    readonly metricsUtil?: MetricsUtil;
    readonly firstRef?: RefObject<any>;
    readonly nextRef?: RefObject<any>;
}

type Props = ProgramCostProps & OwnProps & WithTranslation;

interface State {
    sendToAmazonFee: CurrencyItem; // FBA input
    laborFee: CurrencyItem; // MFN input
    packingFee: CurrencyItem; // MFN input
    shippingToCustomerFee: CurrencyItem; // MFN input
    customerServiceFee: CurrencyItem; // MFN input
    isPeakSelected: boolean; // FBA input
    isFBAInboundConvenienceFeeSelected: boolean; // FBA input
    isBubblewrapFeeSelected: boolean; // FBA input
    isPolybaggingFeeSelected: boolean; // FBA input
    isLabelingFeeSelected: boolean; // FBA input
    isOpaqueBaggingFeeSelected: boolean; // FBA input
    isTapingFeeSelected: boolean; // FBA input
    monthlyStorageFeePerUnit: CurrencyItem; // MFN input
    averageUnitStored: number; // input
    averageUnitSold: number; // input
    goodsSoldCost: CurrencyItem; // input
    miscellaneousCost: CurrencyItem; // input
    vatRate?: number; // input
    ifdRate?: number; // input
}

interface CalculationResult {
    costPerUnit: CurrencyItem; // auto calculated
    amazonFees: CurrencyItem; // auto calculated
    serviceAddonsCost: CurrencyItem; // auto calculated
    fulfillmentCost: CurrencyItem; // auto calculated. for FBA, = fbaFulfillmentFee + sendToAmazonFee + serviceAddonsCost;
    storageCost: CurrencyItem; // auto calculated
    storageCostPerUnitSold: CurrencyItem; // auto calculated
    otherCost: CurrencyItem; // auto calculated, = goodsSoldCost + miscellaneousCost
}

export class ProgramCostComponent extends React.Component<Props, State> {
    private readonly metricsUtil: MetricsUtil;

    // Used for MFN input box focus switch by Enter key
    private readonly packingMaterialInputRef: RefObject<any>;
    private readonly shippingToCustomerInputRef: RefObject<any>;
    private readonly customerServiceInputRef: RefObject<any>;
    private readonly monthlyStorageCostPerUnitInputRef: RefObject<any>;

    // Used for input box focus switch by Enter key, shared by FBA and MFN programs
    private readonly averageInventoryUnitsStoredInputRef: RefObject<any>;
    private readonly estimatedMonthlyUnitsSoldInputRef: RefObject<any>;
    private readonly costOfGoodsSoldInputRef: RefObject<any>;
    private readonly miscellaneousCostInputRef: RefObject<any>;

    private readonly vatRateInputRef: RefObject<any>;
    private readonly ifdRateInputRef: RefObject<any>;

    constructor(props: Props) {
        super(props);

        const defaultCurrencyItem: CurrencyItem = {
            amount: 0, currency: getCurrencyUnitConfig(this.props.countryCode)
        }

        let otherCost = this.props.programFeeResult.otherCost;

        this.state = {
            sendToAmazonFee: defaultCurrencyItem,
            laborFee: defaultCurrencyItem,
            packingFee: defaultCurrencyItem,
            shippingToCustomerFee: defaultCurrencyItem,
            customerServiceFee: defaultCurrencyItem,
            isPeakSelected: false,
            isFBAInboundConvenienceFeeSelected: false,
            isBubblewrapFeeSelected: false,
            isPolybaggingFeeSelected: false,
            isLabelingFeeSelected: false,
            isOpaqueBaggingFeeSelected: false,
            isTapingFeeSelected: false,
            monthlyStorageFeePerUnit: defaultCurrencyItem,
            averageUnitStored: 1,
            averageUnitSold: 1,
            goodsSoldCost: defaultCurrencyItem,
            miscellaneousCost: defaultCurrencyItem,
            vatRate: otherCost?.vatRate,
            ifdRate: otherCost?.ifdRate,
        };

        this.reCalculateAndNotify();

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        this.packingMaterialInputRef = React.createRef<any>();
        this.shippingToCustomerInputRef = React.createRef<any>();
        this.customerServiceInputRef = React.createRef<any>();
        this.monthlyStorageCostPerUnitInputRef = React.createRef<any>();
        this.averageInventoryUnitsStoredInputRef = React.createRef<any>();
        this.estimatedMonthlyUnitsSoldInputRef = React.createRef<any>();
        this.costOfGoodsSoldInputRef = React.createRef<any>();
        this.miscellaneousCostInputRef = React.createRef<any>();
        this.vatRateInputRef = React.createRef<any>();
        this.ifdRateInputRef = React.createRef<any>();

        this.handleButtonGroupChange = this.handleButtonGroupChange.bind(this);
        this.handleSendToAmazonInput = this.handleSendToAmazonInput.bind(this);
        this.handleLaborInput = this.handleLaborInput.bind(this);
        this.handlePackingInput = this.handlePackingInput.bind(this);
        this.handleShippingToCustomerInput = this.handleShippingToCustomerInput.bind(this);
        this.handleCustomerServiceInput = this.handleCustomerServiceInput.bind(this);
        this.handleCostOfGoodsInput = this.handleCostOfGoodsInput.bind(this);
        this.handleMiscellaneousCostInput = this.handleMiscellaneousCostInput.bind(this);
        this.handleVatRateInput = this.handleVatRateInput.bind(this);
        this.handleIfdRateInput = this.handleIfdRateInput.bind(this);
        this.handleMonthlyStorageFeePerUnitInput = this.handleMonthlyStorageFeePerUnitInput.bind(this);
        this.handleAverageUnitStoredInput = this.handleAverageUnitStoredInput.bind(this);
        this.handleAverageUnitSoldInput = this.handleAverageUnitSoldInput.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevState != this.state || prevProps.programFeeResult != this.props.programFeeResult) {
            this.reCalculateAndNotify();
        }
    }

    getCurrencySymbol(): string {
        return getCurrencySymbolConfig(this.props.countryCode);
    };

    private isDataValid = () => {
        const {t, programFeeResult, countryCode, isMFN} = this.props;
        if (programFeeResult == null
            || programFeeResult.otherFeeInfoMap == null
            || !countryCode
            || (!isMFN &&
                (programFeeResult.perUnitNonPeakStorageFee == null || programFeeResult.perUnitPeakStorageFee == null))) {
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter("programCostData", "error");
            }
            return false;
        }
        return true;
    }

    reCalculate(): CalculationResult {
        const {programFeeResult} = this.props;
        const defaultCurrency = getCurrencyUnitConfig(this.props.countryCode);

        const referralFee = programFeeResult.otherFeeInfoMap[FeeType.ReferralFee.toString()];
        const fulfillmentFee = programFeeResult.otherFeeInfoMap[FeeType.FulfillmentFee.toString()];
        const fixedClosingFee = programFeeResult.otherFeeInfoMap[FeeType.FixedClosingFee.toString()];
        const variableClosingFee = programFeeResult.otherFeeInfoMap[FeeType.VariableClosingFee.toString()];

        // service add-on fees
        const fBAInboundConvenienceFee = programFeeResult.otherFeeInfoMap[FeeType.FBAInboundConvenienceFee.toString()];
        const tapingFee = programFeeResult.otherFeeInfoMap[FeeType.TapingFee.toString()];
        const opaqueBaggingFee = programFeeResult.otherFeeInfoMap[FeeType.OpaqueBaggingFee.toString()];
        const labelingFee = programFeeResult.otherFeeInfoMap[FeeType.LabelingFee.toString()];
        const polybaggingFee = programFeeResult.otherFeeInfoMap[FeeType.PolybaggingFee.toString()];
        const bubblewrapFee = programFeeResult.otherFeeInfoMap[FeeType.BubblewrapFee.toString()];

        const fulfillmentFeeAmount = this.getDisplayFeeAmount(fulfillmentFee).amount;
        const referralFeeAmount = this.getDisplayFeeAmount(referralFee).amount;
        const fixedClosingFeeAmount = this.getDisplayFeeAmount(fixedClosingFee).amount;
        const variableClosingFeeAmount = this.getDisplayFeeAmount(variableClosingFee).amount;
        let serviceAddonsFeeAmount = 0;

        // Amazon fees
        const amazonFees = {
            amount: (referralFeeAmount + fixedClosingFeeAmount + variableClosingFeeAmount),
            currency: referralFee ? referralFee.total.currency : defaultCurrency
        };

        // Service add-on fees
        if (this.state.isFBAInboundConvenienceFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(fBAInboundConvenienceFee).amount;
        }
        if (this.state.isTapingFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(tapingFee).amount;
        }
        if (this.state.isOpaqueBaggingFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(opaqueBaggingFee).amount;
        }
        if (this.state.isLabelingFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(labelingFee).amount;
        }
        if (this.state.isPolybaggingFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(polybaggingFee).amount;
        }
        if (this.state.isBubblewrapFeeSelected) {
            serviceAddonsFeeAmount += this.getDisplayFeeAmount(bubblewrapFee).amount;
        }
        const serviceAddonsCost = {
            amount: serviceAddonsFeeAmount,
            currency: defaultCurrency
        };

        // Fulfillment cost
        var fulfillmentCostAmount: number = 0;
        if (!this.props.isMFN) {
            fulfillmentCostAmount = fulfillmentFeeAmount + this.state.sendToAmazonFee.amount + serviceAddonsFeeAmount;
        } else {
            fulfillmentCostAmount = this.state.laborFee.amount + this.state.packingFee.amount
                + this.state.shippingToCustomerFee.amount + this.state.customerServiceFee.amount;
        }
        const fulfillmentCost = {
            amount: fulfillmentCostAmount,
            currency: fulfillmentFee ? fulfillmentFee.total.currency : defaultCurrency
        };

        // Storage cost
        const storageFeePerUnit = this.state.isPeakSelected ?
            this.getDisplayFeeAmount(programFeeResult.perUnitPeakStorageFee) :
            this.getDisplayFeeAmount(programFeeResult.perUnitNonPeakStorageFee);
        var storageCostAmount: number = 0;
        if (!this.props.isMFN) {
            storageCostAmount = storageFeePerUnit.amount * this.state.averageUnitStored;
        } else {
            storageCostAmount = this.state.monthlyStorageFeePerUnit.amount * this.state.averageUnitStored;
        }
        const storageCostPerUnitSoldAmount = this.state.averageUnitSold <= 0 ? storageCostAmount : storageCostAmount / this.state.averageUnitSold;
        const storageCost = {
            amount: storageCostAmount,
            currency: storageFeePerUnit ? storageFeePerUnit.currency : defaultCurrency
        };
        const storageCostPerUnitSold = {
            amount: storageCostPerUnitSoldAmount,
            currency: storageFeePerUnit ? storageFeePerUnit.currency : defaultCurrency
        };

        // Other cost
        let otherCostAmount = this.state.goodsSoldCost.amount + this.state.miscellaneousCost.amount;
        if (this.state.vatRate) {
            otherCostAmount += this.state.vatRate * this.props.productPrice.amount / (1 + this.state.vatRate);
        }
        if (this.state.ifdRate) {
            otherCostAmount += this.state.ifdRate * this.props.productPrice.amount / (1 + this.state.ifdRate);
        }
        const otherCost = {
            amount: otherCostAmount,
            currency: fulfillmentFee ? fulfillmentFee.total.currency : defaultCurrency
        };

        // Cost per unit
        const costPerUnitAmount = amazonFees.amount + fulfillmentCostAmount + storageCostPerUnitSoldAmount + otherCostAmount;
        const costPerUnit = {
            amount: costPerUnitAmount,
            currency: fulfillmentFee ? fulfillmentFee.total.currency : defaultCurrency
        };

        return {
            costPerUnit: costPerUnit,
            amazonFees: amazonFees,
            serviceAddonsCost: serviceAddonsCost,
            fulfillmentCost: fulfillmentCost,
            storageCost: storageCost,
            storageCostPerUnitSold: storageCostPerUnitSold,
            otherCost: otherCost,
        }
    }

    reCalculateAndNotify() {
        const calculateRes = this.reCalculate();
        this.props.handleChangeCostPerUnit(calculateRes.costPerUnit);
    }

    getDisplayFeeAmount(feeInfo: FeeInfo | undefined): CurrencyItem {
        if (feeInfo == null) {
            return {
                amount: 0, currency: getCurrencyUnitConfig(this.props.countryCode)
            };
        }
        return {
            amount: feeInfo.feeAmount.amount + feeInfo.taxAmount.amount,
            currency: feeInfo.feeAmount.currency
        };
    }

    handleCheckboxChange(feeType: FeeType): (event: KatCheckbox.ChangeEvent) => void {
        return (event: KatCheckbox.ChangeEvent) => {
            const checked = event.detail.checked;
            if (this.metricsUtil) {
                this.metricsUtil.publishCounter(feeType.toString() + ".checked", checked.toString());
            }
            switch (feeType) {
                case FeeType.FBAInboundConvenienceFee:
                    this.setState({isFBAInboundConvenienceFeeSelected: checked});
                    break;
                case FeeType.TapingFee:
                    this.setState({isTapingFeeSelected: checked});
                    break;
                case FeeType.OpaqueBaggingFee:
                    this.setState({isOpaqueBaggingFeeSelected: checked});
                    break;
                case FeeType.LabelingFee:
                    this.setState({isLabelingFeeSelected: checked});
                    break;
                case FeeType.PolybaggingFee:
                    this.setState({isPolybaggingFeeSelected: checked});
                    break;
                case FeeType.BubblewrapFee:
                    this.setState({isBubblewrapFeeSelected: checked});
                    break;
                default:
            }
        };
    }

    handleButtonGroupChange(event: KatButtonGroup.ChangeEvent) {
        this.setState({isPeakSelected: event.detail.value === "peak"});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(event.detail.value, "click");
        }
    }

    handleSendToAmazonInput(input: number) {
        this.setState({sendToAmazonFee: {amount: input, currency: this.state.sendToAmazonFee.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("sendToAmazonFee", "input");
        }
    }

    handleLaborInput(input: number) {
        this.setState({laborFee: {amount: input, currency: this.state.laborFee.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("laborFee", "input");
        }
    }

    handlePackingInput(input: number) {
        this.setState({packingFee: {amount: input, currency: this.state.packingFee.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("packingFee", "input");
        }
    }

    handleShippingToCustomerInput(input: number) {
        this.setState({shippingToCustomerFee: {amount: input, currency: this.state.shippingToCustomerFee.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("shippingToCustomerFee", "input");
        }
    }

    handleCustomerServiceInput(input: number) {
        this.setState({customerServiceFee: {amount: input, currency: this.state.customerServiceFee.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("customerServiceFee", "input");
        }
    }

    handleCostOfGoodsInput(input: number) {
        this.setState({goodsSoldCost: {amount: input, currency: this.state.goodsSoldCost.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("goodsSoldCost", "input");
        }
    }

    handleMiscellaneousCostInput(input: number) {
        this.setState({miscellaneousCost: {amount: input, currency: this.state.miscellaneousCost.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("miscellaneousCost", "input");
        }
    }

    handleVatRateInput(input: number) {
        if (this.state.vatRate != null) {
            this.setState({vatRate: input / 100});
        }
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("vatRate", "input");
        }
    }

    handleIfdRateInput(input: number) {
        if (this.state.ifdRate != null) {
            this.setState({ifdRate: input / 100});
        }
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("ifdRate", "input");
        }
    }

    handleMonthlyStorageFeePerUnitInput(input: number) {
        this.setState({monthlyStorageFeePerUnit: {amount: input, currency: this.state.monthlyStorageFeePerUnit.currency}});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("monthlyStorageFeePerUnit", "input");
        }
    }

    handleAverageUnitStoredInput(input: number) {
        this.setState({averageUnitStored: input});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("averageUnitStored", "input");
        }
    }

    handleAverageUnitSoldInput(input: number) {
        this.setState({averageUnitSold: input});
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("averageUnitSold", "input");
        }
    }

    handleExpand(widgetId: string) {
        if (this.metricsUtil && widgetId) {
            this.metricsUtil.publishCounter(widgetId, "expand");
        }
    }

    handleCollapse(widgetId: string) {
        if (this.metricsUtil && widgetId) {
            this.metricsUtil.publishCounter(widgetId, "collapse");
        }
    }

    calculateVATIFD(rate: number): CurrencyItem {
        // product price = raw price * (1 + rate)
        return {
            amount: rate * this.props.productPrice.amount / (1 + rate), currency: getCurrencyUnitConfig(this.props.countryCode)
        };
    }

    generateInputFeeBlockContent(labelStringId: string, fee: CurrencyItem, handleInput: Function, ref?: RefObject<any>, nextRef?: RefObject<any>, popoverStringId?: string) {
        return (
            <div className="input-block input-block-padding">
                {this.generateLabelWithOptionalPopover(labelStringId, popoverStringId)}
                <div className="input-block-group-box">
                    <KatInputGroup className="input-block-inputgroup">
                        <KatLabel text={this.getCurrencySymbol()} className="input-block-symbol"/>
                        <NumberKatInput isDisabled={false} className="input-block-input-price" minValue={0} isFloat={true}
                                        value={fee.amount != 0 ? fee.amount : undefined} ref={ref} nextRef={nextRef}
                                        onInput={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                handleInput(input);
                                            }
                                        }}/>
                    </KatInputGroup>
                </div>
            </div>
        );
    }

    generateInputFeeRateWithResultBlockContent(labelStringId: string, rate: number, handleInput: Function, ref?: RefObject<any>, nextRef?: RefObject<any>, popoverStringId?: string) {
        return (
            <div className="input-block input-block-padding">
                {this.generateLabelWithOptionalPopover(labelStringId, popoverStringId)}
                <div className="input-block-group-box">
                    <KatInputGroup className="input-block-inputgroup">
                        <NumberKatInput isDisabled={false} className="input-block-input-rate" minValue={0} isFloat={true} value={rate * 100}
                                        ref={ref} nextRef={nextRef}
                                        onInput={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                handleInput(input);
                                            }
                                        }}/>
                        <KatLabel text="%" className="input-block-symbol"/>
                    </KatInputGroup>
                    <KatLabel text={getCurrencyString(this.calculateVATIFD(rate))} className="subsection-content-currency"/>
                </div>
            </div>
        );
    }

    generateOptionalStaticFeeBlockContent(feeType: FeeType, feeInfo?: FeeInfo, feeNameStringId?: string, popoverStringId?: string, feeHelpLink?: string) {
        if (feeInfo == null) {
            return (<></>);
        }

        const labelStringId = feeNameStringId ? feeNameStringId : feeInfo.feeNameStringId;
        return (
            <div className="service-addon-box-content">
                <div className="checkbox-with-label">
                    <KatCheckbox className="input-block-text" label=" " onChange={this.handleCheckboxChange(feeType)}/>
                    {feeHelpLink ?
                        this.generateLinkLabel(this.generateLabelWithOptionalPopover(labelStringId, popoverStringId), feeHelpLink) :
                        this.generateLabelWithOptionalPopover(labelStringId, popoverStringId)}
                </div>
                <KatLabel text={getCurrencyString(this.getDisplayFeeAmount(feeInfo))} className="subsection-content-currency" />
            </div>
        );
    }

    generateInputNumberBlockContent(labelStringId: string, defaultValue: number, handleInput: Function, ref?: RefObject<any>, nextRef?: RefObject<any>, popoverStringId?: string) {
        const {t} = this.props;
        return (
            <div className="input-block input-block-padding">
                {this.generateLabelWithOptionalPopover(labelStringId, popoverStringId)}
                <NumberKatInput isDisabled={false} className="input-block-inputbox" minValue={0} isFloat={false} isMandatory={true}
                                value={defaultValue != 0 ? defaultValue : undefined} ref={ref} nextRef={nextRef}
                                onInput={(input: number, hasError: boolean): void => {
                                    if (!hasError) {
                                        handleInput(input);
                                    }
                                }}/>
            </div>
        );
    }

    generateStaticFeeBlockContentWithFeeInfo(feeInfo?: FeeInfo, feeNameStringId?: string, popoverStringId?: string, feeHelpLink?: string) {
        if (feeInfo == null) {
            return (<></>);
        }
        return this.generateStaticFeeBlockContent(feeNameStringId ? feeNameStringId : feeInfo.feeNameStringId, this.getDisplayFeeAmount(feeInfo), popoverStringId, feeHelpLink);
    }

    generateStaticFeeBlockContent(labelStringId: string, currencyItem: CurrencyItem, popoverStringId?: string, feeHelpLink?: string) {
        const {t} = this.props;
        return (
            <div className="input-block input-block-padding-plus">
                {feeHelpLink ?
                    this.generateLinkLabel(this.generateLabelWithOptionalPopover(labelStringId, popoverStringId), feeHelpLink) :
                    this.generateLabelWithOptionalPopover(labelStringId, popoverStringId)}
                <KatLabel text={getCurrencyString(currencyItem)} className="subsection-content-currency"/>
            </div>
        );
    }

    generateStaticWarningContent(labelStringId: string) {
        const {t} = this.props;
        return (
            <div className="input-block input-block-padding">
                <KatLabel text={t(labelStringId)} className="input-block-text-warning"/>
            </div>
        );
    }

    generateLinkLabel(content: any, linkUrl: string) {
        return (
            <KatLink href={linkUrl} target="_blank">
                {content}
            </KatLink>
        );
    }

    generateLabelWithOptionalPopover(labelStringId: string, popoverStringId?: string) {
        const {t} = this.props;
        if (popoverStringId) {
            return (
                <KatPopover triggerType="hover" katAriaBehavior="tooltip" position="bottom-start">
                    <KatLabel slot="trigger" text={t(labelStringId)} className="input-block-text"/>
                    <KatLabel text={t(popoverStringId)} className="popover-content"/>
                </KatPopover>
            );
        } else {
            return (
                <KatLabel text={t(labelStringId)} className="input-block-text"/>
            );
        }
    }

    render() {
        const {t, programFeeResult} = this.props;

        const calculationResult = this.reCalculate();

        const referralFee = programFeeResult.otherFeeInfoMap[FeeType.ReferralFee.toString()];
        const fulfillmentFee = programFeeResult.otherFeeInfoMap[FeeType.FulfillmentFee.toString()];
        const fixedClosingFee = programFeeResult.otherFeeInfoMap[FeeType.FixedClosingFee.toString()];
        const variableClosingFee = programFeeResult.otherFeeInfoMap[FeeType.VariableClosingFee.toString()];

        // service add-on fees
        const fBAInboundConvenienceFee = programFeeResult.otherFeeInfoMap[FeeType.FBAInboundConvenienceFee.toString()];
        const tapingFee = programFeeResult.otherFeeInfoMap[FeeType.TapingFee.toString()];
        const opaqueBaggingFee = programFeeResult.otherFeeInfoMap[FeeType.OpaqueBaggingFee.toString()];
        const labelingFee = programFeeResult.otherFeeInfoMap[FeeType.LabelingFee.toString()];
        const polybaggingFee = programFeeResult.otherFeeInfoMap[FeeType.PolybaggingFee.toString()];
        const bubblewrapFee = programFeeResult.otherFeeInfoMap[FeeType.BubblewrapFee.toString()];

        const storageFeePerUnit = this.state.isPeakSelected ?
            this.getDisplayFeeAmount(programFeeResult.perUnitPeakStorageFee) :
            this.getDisplayFeeAmount(programFeeResult.perUnitNonPeakStorageFee);

        const amazonFeesContent = (
            <KatExpander label={t("SC_FBA_Calc_title_amazonFees")} expanded={false} className="section-expander"
                         onExpand={() => this.handleExpand("amazonFees")} onCollapse={() => this.handleCollapse("amazonFees")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.amazonFees)} className="expander-badge-currency"/>
                </div>
                <div className="section-expander-content">
                    {this.generateStaticFeeBlockContentWithFeeInfo(referralFee, "FS-ReferralFee-FullName", undefined, getHelpPageLink(this.props.countryCode, FeeType.ReferralFee.toString()))}
                    {this.generateStaticFeeBlockContentWithFeeInfo(fixedClosingFee, undefined, undefined, getHelpPageLink(this.props.countryCode, FeeType.FixedClosingFee.toString()))}
                    {this.generateStaticFeeBlockContentWithFeeInfo(variableClosingFee, undefined, undefined, getHelpPageLink(this.props.countryCode, FeeType.VariableClosingFee.toString()))}
                </div>
            </KatExpander>
        );

        const serviceAddonsContent = (
            <div>
                {this.generateStaticFeeBlockContent("SC_FBA_Calc_label_serviceAddons", calculationResult.serviceAddonsCost, undefined, undefined)}
                <div className="service-addon-box">
                    {this.generateOptionalStaticFeeBlockContent(FeeType.FBAInboundConvenienceFee, fBAInboundConvenienceFee, "SC_FBA_Calc_label_placement", undefined, getHelpPageLink(this.props.countryCode, FeeType.FBAInboundConvenienceFee.toString()))}
                    {this.generateOptionalStaticFeeBlockContent(FeeType.OpaqueBaggingFee, opaqueBaggingFee, "SC_FBA_Calc_label_opaueBagging", undefined, getHelpPageLink(this.props.countryCode, "PrepFees"))}
                    {this.generateOptionalStaticFeeBlockContent(FeeType.TapingFee, tapingFee, "SC_FBA_Calc_label_tape", undefined, getHelpPageLink(this.props.countryCode, "PrepFees"))}
                    {this.generateOptionalStaticFeeBlockContent(FeeType.LabelingFee, labelingFee, "SC_FBA_Calc_label_label", undefined, getHelpPageLink(this.props.countryCode, "PrepFees"))}
                    {this.generateOptionalStaticFeeBlockContent(FeeType.PolybaggingFee, polybaggingFee, "SC_FBA_Calc_label_bagging", undefined, getHelpPageLink(this.props.countryCode, "PrepFees"))}
                    {this.generateOptionalStaticFeeBlockContent(FeeType.BubblewrapFee, bubblewrapFee, "SC_FBA_Calc_label_bubbleWrap", undefined, getHelpPageLink(this.props.countryCode, "PrepFees"))}
                </div>
            </div>
        );

        const fbaFulfillmentCostContent = (
            <KatExpander label={t("SC_FBA_Calc_fulfillCost")} className="section-expander"
                         onExpand={() => this.handleExpand("fbaFulfillCost")} onCollapse={() => this.handleCollapse("fbaFulfillCost")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.fulfillmentCost)} className="expander-badge-currency"/>
                </div>
                <div className="section-expander-content">
                    {this.generateStaticFeeBlockContentWithFeeInfo(fulfillmentFee, "SC_FBA_Calc_label_FBAFulfillmentFees", undefined, getHelpPageLink(this.props.countryCode, FeeType.FulfillmentFee.toString()))}
                    {serviceAddonsContent}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_sendToAmazon", this.state.sendToAmazonFee, this.handleSendToAmazonInput, this.props.firstRef, this.averageInventoryUnitsStoredInputRef, "SC_FBA_Calc_tt_inboundShip_new")}
                </div>
            </KatExpander>
        );

        const mfnFulfillmentCostContent = (
            <KatExpander label={t("SC_FBA_Calc_fulfillCost")} expanded={true} className="section-expander"
                         onExpand={() => this.handleExpand("mfnFulfillCost")} onCollapse={() => this.handleCollapse("mfnFulfillCost")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.fulfillmentCost)}
                              className={calculationResult.fulfillmentCost.amount <= 0 ? "expander-badge-currency-red" : "expander-badge-currency"}/>
                </div>
                <div className="section-expander-content">
                    {calculationResult.fulfillmentCost.amount <= 0 ? this.generateStaticWarningContent("SC_FBA_Calc_tip_enterFulfillmentCost") : <></>}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_labor", this.state.laborFee, this.handleLaborInput, this.props.firstRef, this.packingMaterialInputRef)}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_packingMaterial", this.state.packingFee, this.handlePackingInput, this.packingMaterialInputRef, this.shippingToCustomerInputRef)}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_shipToCustomer", this.state.shippingToCustomerFee, this.handleShippingToCustomerInput, this.shippingToCustomerInputRef, this.customerServiceInputRef)}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_customerService", this.state.customerServiceFee, this.handleCustomerServiceInput, this.customerServiceInputRef, this.monthlyStorageCostPerUnitInputRef)}
                </div>
            </KatExpander>
        );

        const fbaStorageTimeRangeContent = (
            <div className="input-block-justify-end input-block-padding">
                <div className="input-block-group-box">
                    <KatButtonGroup value={this.state.isPeakSelected ? "peak" : "nonPeak"} onChange={this.handleButtonGroupChange}>
                        <KatButton label={t("SC_FBA_Calc_label_nonPeakPeriod")} value="nonPeak"/>
                        <KatButton label={t("SC_FBA_Calc_label_peakPeriod")} value="peak"/>
                    </KatButtonGroup>
                </div>
            </div>
        );

        const fbaStorageCostContent = (

            < KatExpander label={t("SC_FBA_Calc_StorageCost")} expanded={true} className="section-expander"
                          onExpand={() => this.handleExpand("fbaStorageCost")} onCollapse={() => this.handleCollapse("fbaStorageCost")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.storageCost)} className="expander-badge-currency"/>
                </div>
                <div className="section-expander-content">
                    {fbaStorageTimeRangeContent}
                    {this.generateStaticFeeBlockContent("SC_FBA_Calc_MSCPU", storageFeePerUnit, "SC_FBA_Calc_MSCPU_popover", getHelpPageLink(this.props.countryCode, "MonthlyStorageFee"))}
                    {this.generateInputNumberBlockContent("SC_FBA_Calc_AIUS", this.state.averageUnitStored, this.handleAverageUnitStoredInput, this.averageInventoryUnitsStoredInputRef, this.estimatedMonthlyUnitsSoldInputRef, "SC_FBA_Calc_AIUS_popover")}
                    {this.generateInputNumberBlockContent("SC_FBA_Calc_label_estimatedMontlyUnitsSold", this.state.averageUnitSold, this.handleAverageUnitSoldInput, this.estimatedMonthlyUnitsSoldInputRef, this.costOfGoodsSoldInputRef)}
                    {this.generateStaticFeeBlockContent("SC_FBA_Calc_SCPUS", calculationResult.storageCostPerUnitSold, "SC_FBA_Calc_SCPUS_Popover")}
                </div>
            </KatExpander>
        );

        const mfnStorageCostContent = (
            <KatExpander label={t("SC_FBA_Calc_StorageCost")} expanded={true} className="section-expander"
                         onExpand={() => this.handleExpand("mfnStorageCost")} onCollapse={() => this.handleCollapse("mfnStorageCost")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.storageCost)}
                              className={calculationResult.storageCost.amount <= 0 ? "expander-badge-currency-red" : "expander-badge-currency"}/>
                </div>
                <div className="section-expander-content">
                    {calculationResult.storageCost.amount <= 0 ? this.generateStaticWarningContent("SC_FBA_Calc_tip_enterStorageCost") : <></>}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_MSCPU", this.state.monthlyStorageFeePerUnit, this.handleMonthlyStorageFeePerUnitInput, this.monthlyStorageCostPerUnitInputRef, this.averageInventoryUnitsStoredInputRef, "SC_FBA_Calc_MSCPU_popover")}
                    {this.generateInputNumberBlockContent("SC_FBA_Calc_AIUS", this.state.averageUnitStored, this.handleAverageUnitStoredInput, this.averageInventoryUnitsStoredInputRef, this.estimatedMonthlyUnitsSoldInputRef, "SC_FBA_Calc_AIUS_popover")}
                    {this.generateInputNumberBlockContent("SC_FBA_Calc_label_estimatedMontlyUnitsSold", this.state.averageUnitSold, this.handleAverageUnitSoldInput, this.estimatedMonthlyUnitsSoldInputRef, this.state.vatRate ? this.vatRateInputRef : (this.state.ifdRate ? this.ifdRateInputRef : this.costOfGoodsSoldInputRef))}
                    {this.generateStaticFeeBlockContent("SC_FBA_Calc_SCPUS", calculationResult.storageCostPerUnitSold, "SC_FBA_Calc_SCPUS_Popover")}
                </div>
            </KatExpander>
        );

        const otherCostContent = (
            <KatExpander label={t("SC_FBA_Calc_title_otherCosts")} expanded={true} className="section-expander"
                         onExpand={() => this.handleExpand("otherCosts")} onCollapse={() => this.handleCollapse("otherCosts")}>
                <div slot="badge">
                    <KatLabel text={getCurrencyString(calculationResult.otherCost)} className="expander-badge-currency"/>
                </div>
                <div className="section-expander-content">
                    {this.state.vatRate != null && this.generateInputFeeRateWithResultBlockContent("SC_FBA_Calc_label_vat", this.state.vatRate, this.handleVatRateInput, this.vatRateInputRef, this.state.ifdRate ? this.ifdRateInputRef : this.costOfGoodsSoldInputRef, "SC_FBA_Calc_tip_vat")}
                    {this.state.ifdRate != null && this.generateInputFeeRateWithResultBlockContent("SC_FBA_Calc_label_ifd", this.state.ifdRate, this.handleIfdRateInput, this.ifdRateInputRef, this.costOfGoodsSoldInputRef, "SC_FBA_Calc_tip_ifd")}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_costOfGoodsSold", this.state.goodsSoldCost, this.handleCostOfGoodsInput, this.costOfGoodsSoldInputRef, this.miscellaneousCostInputRef)}
                    {this.generateInputFeeBlockContent("SC_FBA_Calc_label_miscellaneousCost", this.state.miscellaneousCost, this.handleMiscellaneousCostInput, this.miscellaneousCostInputRef, this.props.nextRef)}
                </div>
            </KatExpander>
        );

        if (this.isDataValid()) {
            return (
                <div className="cost-section">
                    <div className="section-content">
                        {amazonFeesContent}
                        {!this.props.isMFN ? <> {fbaFulfillmentCostContent} </> : <>{mfnFulfillmentCostContent}</>}
                        {!this.props.isMFN ? <> {fbaStorageCostContent} </> : <>{mfnStorageCostContent}</>}
                        {otherCostContent}
                    </div>
                </div>
            );
        } else {
            return (<></>)
        }
    }
}

export default withTranslation()(ProgramCostComponent);
